import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pegasus, toEnum } from 'corso-types';
import { useCallback, useMemo } from 'react';
import api from '~/api';
import { useToast } from '~/hooks/useToast';
import { useStoreId } from '~/providers/StoreUserProvider';
import { Automation, AutomationCreate, AutomationUpdate } from '~/types';
import { isErrorMessage } from '~/utils/errorResponse';
import { groupBy } from '~/utils/map';

// TODO revise usage of mutation and queries to be more consistent, and abstract optimistic/error handling

function useQueryKey() {
  const storeId = useStoreId();
  return ['automationRules', storeId] as const;
}

export function useAutomations() {
  const queryKey = useQueryKey();
  return useQuery({
    queryKey,
    initialData: [],
    queryFn: ({ queryKey: [, storeId] }) =>
      api
        .store(storeId)
        .automations.list()
        .then((automations) => automations.sort((a, b) => a.id - b.id)),
  });
}

function useInvalidateRelatedQueries() {
  const queryClient = useQueryClient();
  const queryKey = useQueryKey();

  return useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey });
    await queryClient.invalidateQueries({
      queryKey: ['inspectionSettings'],
    });
  }, [queryClient, queryKey]);
}

export function useAutomationUpsert() {
  const storeId = useStoreId();

  const toast = useToast();
  const invalidateRelatedQueries = useInvalidateRelatedQueries();

  return useMutation({
    mutationFn: (rule: AutomationCreate | AutomationUpdate) =>
      'id' in rule
        ? api.store(storeId).automations.update(`${rule.id}`, rule)
        : api.store(storeId).automations.create(rule),

    onSuccess: () => invalidateRelatedQueries(),
    onError: (error) => {
      toast.show(
        isErrorMessage(error)
          ? error.message
          : 'Something went wrong. Please try again.',
      );
    },
  });
}

export function useAutomationDelete() {
  const storeId = useStoreId();

  const toast = useToast();
  const invalidateRelatedQueries = useInvalidateRelatedQueries();

  return useMutation({
    mutationFn: (storeRuleId: Automation['id']) =>
      api.store(storeId).automations.delete(`${storeRuleId}`),
    onSuccess: () => invalidateRelatedQueries(),
    onError: (error) => {
      toast.show(
        isErrorMessage(error)
          ? error.message
          : 'Something went wrong. Please try again.',
      );
    },
  });
}

/** A simplified abstraction to more easily toggle a rule. */
export function useToggleAutomation() {
  const { mutate: upsertRule } = useAutomationUpsert();
  return useCallback(
    (automation: Automation) => {
      upsertRule({
        ...automation,
        isEnabled: !automation.isEnabled,
      });
    },
    [upsertRule],
  );
}

function useAutomationsBy<T extends PropertyKey>(
  group: (automation: Automation) => T,
) {
  const { data: automations } = useAutomations();
  const automationsBy = useMemo(
    () => groupBy(automations, group),
    [automations, group],
  );
  return automationsBy;
}

function useAutomationsByHook() {
  return useAutomationsBy((rule) => toEnum(rule.hook, Pegasus.Hook));
}

export function useAutomationsForHook(hook: Pegasus.Hook) {
  const automationsByHook = useAutomationsByHook();

  return useMemo(
    () => automationsByHook.get(toEnum(hook, Pegasus.Hook)) ?? [],
    [automationsByHook, hook],
  );
}

/**
 * Priority is primarily emphasized around the hook association of a rule.
 * Evaluate the current rules for the hook, and return the next priority for them.
 */
export function useNextPriorityByHook(hook: Pegasus.Hook) {
  const automationsForHook = useAutomationsForHook(toEnum(hook, Pegasus.Hook));

  const nextPriority =
    Math.max(0, ...automationsForHook.map((rule) => rule.rule.priority)) + 1;

  return nextPriority;
}

export function useAutomationsByEventType() {
  return useAutomationsBy((rule) =>
    toEnum(rule.rule.event.type, Pegasus.EventType),
  );
}

export function useAutomationsForEventType(eventType: Pegasus.EventType) {
  const automationsByEventType = useAutomationsBy((rule) =>
    toEnum(rule.rule.event.type, Pegasus.EventType),
  );

  return useMemo(
    () => automationsByEventType.get(eventType) ?? [],
    [automationsByEventType, eventType],
  );
}
